import { Accordion, Button, Loading, TextField } from '../../../../components';
import React, { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useFormik } from 'formik';

export const EmailDryRun = (props: {
  selectedTemplate: any;
}): React.JSX.Element => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [applicationNos, setApplicationNos] = React.useState<string[]>([]);
  const [hkpfsEmailRecords, setHkpfsEmailRecords] = React.useState<any[]>([]);

  const [results, setResults] = React.useState<any[] | null>(null);

  const { selectedTemplate } = props;

  const [directEmailWithTemplate] = useMutation(gql`
    mutation directEmailWithTemplate(
      $templateId: String!
      $dryRunEmailAddress: [String!]
      $input: JSONObject!
    ) {
      directEmailWithTemplate(
        templateId: $templateId
        dryRunEmailAddress: $dryRunEmailAddress
        input: $input
      )
    }
  `);

  const { refetch } = useQuery(
    gql`
      query HKPFSEmailDryRun($input: [String!]!) {
        HKPFSEmailDryRun(applicationNos: $input)
      }
    `,
    { variables: { input: applicationNos }, skip: true },
  );

  const _onClickFetch = async (): Promise<void> => {
    setLoading(true);
    const { data } = await refetch();
    setHkpfsEmailRecords(data?.HKPFSEmailDryRun);
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      dryRunEmailAddresses: '',
      ...(selectedTemplate.placeholder ?? []).reduce((acc: any, cur: any) => {
        acc[cur] = '';
        return acc;
      }, {}),
    },
    onSubmit: async values => {
      setLoading(true);
      await directEmailWithTemplate({
        variables: {
          templateId: selectedTemplate.templateId,
          dryRunEmailAddress: values.dryRunEmailAddresses
            .split('||')
            .map((e: string) => e.trim()),
          input: selectedTemplate.placeholder?.reduce((acc: any, cur: any) => {
            acc[cur] = values[cur];
            return acc;
          }, {}),
        },
      });
      setLoading(false);
    },
  });

  useEffect(() => {
    formik.resetForm({
      values: {
        dryRunEmailAddresses: '',
        ...(selectedTemplate.placeholder ?? []).reduce((acc: any, cur: any) => {
          acc[cur] = '';
          return acc;
        }, {}),
      },
    });
  }, [selectedTemplate]);

  const downloadTemplate = async (): Promise<void> => {
    const processRowData = (rowData: any): string => {
      const { email, to, cc, bcc, ...rest } = rowData;

      const strings = [
        email ?? to,
        cc,
        bcc,
        ...selectedTemplate.placeholder.map((key: string) => rest[key]),
      ].join(',');
      return strings + '\n';
    };

    // export as csv
    const csv =
      ['email', 'cc', 'bcc']
        .concat(selectedTemplate.placeholder ?? [])
        .join(',') + '\n';
    const rows = hkpfsEmailRecords?.map(processRowData).join('') ?? '';

    const blob = new Blob([csv + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template.csv';
    a.click();
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleUploadClick = (): void => {
    if (Object.keys(selectedTemplate).length === 0) return;
    fileInputRef.current?.click();
  };

  const uploadTemplate = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<any> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = async event => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        const lines = content.split('\n');
        const headers = lines[0].split(',');

        const json = lines.slice(1).map(line => {
          if (line.trim() === '') {
            return null;
          }

          const values = line.split(',');
          const obj: Record<string, any> = {};
          headers.forEach((header, index) => {
            const currentValue = values?.[index]?.trim();
            if (currentValue?.includes('||')) {
              obj[header?.trim()] = currentValue
                ?.split('||')
                ?.map((e: string) => e?.trim());
              return;
            }
            obj[header?.trim()] = [values?.[index]?.trim()];
          });
          return obj;
        });

        setLoading(true);
        // use the mutation, note there is no dry run email address
        const sentResults = await directEmailWithTemplate({
          variables: {
            templateId: selectedTemplate.templateId,
            input: json,
          },
        });

        setResults(sentResults.data?.directEmailWithTemplate);
        // clear the input
        e.target.value = '';
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return loading ? (
    <Loading />
  ) : (
    <div>
      <Accordion
        title={'HKPFS Information Gathering'}
        details={
          <div>
            <p className="m-4">
              <i>Please use linebreaks to provide application numbers. </i>
            </p>
            <TextField
              multiline
              fullWidth
              label="Application Nos"
              value={applicationNos.join('\n')}
              onChange={async e => {
                setApplicationNos(e.target.value.split('\n'));
              }}
            />
            <p className="m-4">
              <i>
                {(hkpfsEmailRecords ?? [])?.length} record(s) has been fetched.
                Click the DOWNLOAD TEMPLATE button will give the real data to be
                used in the email sending.
              </i>
              <br />
              <i>
                Please select Email Template before downloading template csv
                file.
              </i>
            </p>
            <Button
              onClick={async () => {
                await _onClickFetch();
              }}
            >
              Fetch Applicant Data
            </Button>

            <Button
              onClick={downloadTemplate}
              disabled={
                Object.keys(selectedTemplate).length === 0 ||
                hkpfsEmailRecords.length === 0
              }
            >
              Download Template
            </Button>
            <>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={uploadTemplate}
              />
              <Button
                onClick={handleUploadClick}
                disabled={Object.keys(selectedTemplate).length === 0}
              >
                Upload Batch Email
              </Button>
            </>
          </div>
        }
      />
      {results && (
        <Accordion
          title={'Email Sending Status'}
          details={
            <table className="m-4">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Sent</th>
                  <th style={{ textAlign: 'left' }}>Email</th>
                  <th style={{ textAlign: 'left' }}>CC</th>
                  <th style={{ textAlign: 'left' }}>BCC</th>
                  <th style={{ textAlign: 'left' }}>Applicant Name</th>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Month</th>
                  <th style={{ textAlign: 'left' }}>Year</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, index) => (
                  <tr key={index}>
                    <td>{result.hasSent === true ? '✔' : '✘'}</td>
                    <td>{result.email}</td>
                    <td>{result.cc}</td>
                    <td>{result.bcc}</td>
                    <td>{result.applicantName}</td>
                    <td>{result.date}</td>
                    <td>{result.month}</td>
                    <td>{result.year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        />
      )}
      <Accordion
        title="Dry Run"
        className="m-4"
        details={
          <div>
            <form onSubmit={formik.handleSubmit}>
              <div className="flex flex-row flex-wrap justify-start space-x-4">
                <div className="w-0" />
                <TextField
                  name="dryRunEmailAddresses"
                  label="Dry Run Email Address"
                  className="w-[40%]"
                  value={formik.values.dryRunEmailAddresses}
                  onChange={formik.handleChange}
                />
                <div className="w-[40%]" />
                {selectedTemplate?.placeholder?.map((placeholder: string) => {
                  // process the name, camel case to general English
                  const camelCaseToEnglish = (camelCase: string): string => {
                    return camelCase
                      ?.replace(/([A-Z])/g, ' $1') // Insert space before each uppercase letter
                      ?.replace(/^./, str => str.toUpperCase()) // Capitalize the first letter
                      ?.trim(); // Remove any leading/trailing spaces
                  };
                  return (
                    <TextField
                      key={placeholder}
                      name={placeholder}
                      label={camelCaseToEnglish(placeholder)}
                      value={formik.values[placeholder]}
                      onChange={formik.handleChange}
                      className="w-[40%] mt-4 mb-4"
                    />
                  );
                })}
                <div className="w-[100%]" />
                <Button variant="outlined" type="submit">
                  Submit
                </Button>
                <Button onClick={downloadTemplate}>Download Template</Button>
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={uploadTemplate}
                  />
                  <Button onClick={handleUploadClick}>
                    Upload Batch Email
                  </Button>
                </>
              </div>
            </form>
            <div className="italic p-4">
              <ul>
                <li>
                  Please note for non-production environment, the emails will
                  send to whitelisted users.
                </li>
                <li>
                  For array-like items, please use double pipe sign to separate
                  the input.
                </li>
                <li>
                  Please utilize the email CSV template to comply with the
                  template requirement. Each row will be triggered as one email
                  entity.
                </li>
                <li>It is suggested not to exceed 100 emails in one batch.</li>
                <li>Tracking sending status will be provided later.</li>
              </ul>
            </div>
          </div>
        }
      />
    </div>
  );
};
